import { FRAMEBUFFER_HEIGHT, FRAMEBUFFER_WIDTH } from "../emulator.js";

import type { Renderer, RendererDebugState } from "./types.js";

const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;

void main() {
  v_texCoord = a_texCoord;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SOURCE = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_texture;

void main() {
  gl_FragColor = texture2D(u_texture, v_texCoord);
}
`;

export class WebGLRenderer implements Renderer {
  private readonly gl: WebGLRenderingContext;
  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private buffer: WebGLBuffer | null = null;
  private presentCount = 0;
  private contextLost = false;
  private debugState: RendererDebugState = {
    backend: "webgl",
    fallbackReason: null
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    const gl =
      this.canvas.getContext("webgl", { antialias: false, preserveDrawingBuffer: true }) ??
      (this.canvas.getContext("experimental-webgl", {
        antialias: false,
        preserveDrawingBuffer: true
      }) as WebGLRenderingContext | null);
    if (gl === null) {
      throw new Error("WebGL context is not available.");
    }

    this.canvas.width = FRAMEBUFFER_WIDTH;
    this.canvas.height = FRAMEBUFFER_HEIGHT;
    this.gl = gl;
    this.initializeResources();
    this.canvas.addEventListener("webglcontextlost", this.handleContextLost);
    this.canvas.addEventListener("webglcontextrestored", this.handleContextRestored);
  }

  present(framebuffer: Uint8ClampedArray): void {
    if (this.contextLost) {
      return;
    }
    if (this.texture === null) {
      throw new Error("WebGL texture is unavailable.");
    }

    const gl = this.gl;
    gl.viewport(0, 0, FRAMEBUFFER_WIDTH, FRAMEBUFFER_HEIGHT);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      FRAMEBUFFER_WIDTH,
      FRAMEBUFFER_HEIGHT,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      framebuffer
    );
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    this.presentCount++;
  }

  private initializeResources(): void {
    this.program = this.createProgram(VERTEX_SHADER_SOURCE, FRAGMENT_SHADER_SOURCE);
    this.texture = this.createTexture();
    this.buffer = this.createVertexBuffer();
    this.configurePipeline();
  }

  getPresentCount(): number {
    return this.presentCount;
  }

  getDebugState(): RendererDebugState {
    return { ...this.debugState };
  }

  private createShader(type: number, source: string): WebGLShader {
    const shader = this.gl.createShader(type);
    if (shader === null) {
      throw new Error("WebGL shader allocation failed.");
    }

    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS) === true) {
      return shader;
    }

    const log = this.gl.getShaderInfoLog(shader) ?? "unknown shader compile error";
    this.gl.deleteShader(shader);
    throw new Error(`WebGL shader compile failed: ${log}`);
  }

  private createProgram(vertexSource: string, fragmentSource: string): WebGLProgram {
    const vertexShader = this.createShader(this.gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, fragmentSource);
    const program = this.gl.createProgram();
    if (program === null) {
      this.gl.deleteShader(vertexShader);
      this.gl.deleteShader(fragmentShader);
      throw new Error("WebGL program allocation failed.");
    }

    this.gl.attachShader(program, vertexShader);
    this.gl.attachShader(program, fragmentShader);
    this.gl.linkProgram(program);
    this.gl.deleteShader(vertexShader);
    this.gl.deleteShader(fragmentShader);
    if (this.gl.getProgramParameter(program, this.gl.LINK_STATUS) === true) {
      return program;
    }

    const log = this.gl.getProgramInfoLog(program) ?? "unknown program link error";
    this.gl.deleteProgram(program);
    throw new Error(`WebGL program link failed: ${log}`);
  }

  private createTexture(): WebGLTexture {
    const texture = this.gl.createTexture();
    if (texture === null) {
      throw new Error("WebGL texture allocation failed.");
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.pixelStorei(this.gl.UNPACK_ALIGNMENT, 1);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
    this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.RGBA,
      FRAMEBUFFER_WIDTH,
      FRAMEBUFFER_HEIGHT,
      0,
      this.gl.RGBA,
      this.gl.UNSIGNED_BYTE,
      null
    );
    return texture;
  }

  private createVertexBuffer(): WebGLBuffer {
    const buffer = this.gl.createBuffer();
    if (buffer === null) {
      throw new Error("WebGL vertex buffer allocation failed.");
    }

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
    this.gl.bufferData(
      this.gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1, 0, 1,
        1, -1, 1, 1,
        -1, 1, 0, 0,
        1, 1, 1, 0
      ]),
      this.gl.STATIC_DRAW
    );
    return buffer;
  }

  private configurePipeline(): void {
    const gl = this.gl;
    if (this.program === null || this.texture === null || this.buffer === null) {
      throw new Error("WebGL resources are unavailable.");
    }

    const positionLocation = gl.getAttribLocation(this.program, "a_position");
    const texCoordLocation = gl.getAttribLocation(this.program, "a_texCoord");
    const textureLocation = gl.getUniformLocation(this.program, "u_texture");
    if (positionLocation < 0 || texCoordLocation < 0 || textureLocation === null) {
      throw new Error("WebGL shader bindings are incomplete.");
    }

    gl.useProgram(this.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 16, 8);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(textureLocation, 0);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  private readonly handleContextLost = (event: Event): void => {
    event.preventDefault();
    this.contextLost = true;
    this.debugState = {
      backend: "webgl",
      fallbackReason: "WebGL context lost; waiting for restoration."
    };
  };

  private readonly handleContextRestored = (): void => {
    try {
      this.contextLost = false;
      this.initializeResources();
      this.debugState = {
        backend: "webgl",
        fallbackReason: null
      };
    } catch (error) {
      this.contextLost = true;
      this.debugState = {
        backend: "webgl",
        fallbackReason:
          error instanceof Error && error.message.trim() !== ""
            ? `WebGL context restore failed: ${error.message.trim()}`
            : "WebGL context restore failed."
      };
    }
  };
}
